import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** 调试用 label，写到 fallback 屏顶上方，方便分辨是哪个路由挂了。 */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * 单层 error boundary。React 18 默认把 render 异常往 root 抛，配合
 * 没有 boundary 的 SPA 会直接卸载整棵子树（页面变成空白）。这个组件
 * 包在路由 element 外层，把错误堆栈打到屏上，比让用户对着空白瞪眼好。
 *
 * 只用于带 input/hook 较密集的页面（设置、网络等）。其它路由保留默认
 * 行为。
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', this.props.label, error, info?.componentStack ?? '');
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="m-4 rounded-md border border-rose-500/40 bg-rose-500/5 p-4 text-sm">
        <h2 className="mb-2 font-semibold text-rose-600 dark:text-rose-400">
          页面渲染失败 {this.props.label ? `· ${this.props.label}` : ''}
        </h2>
        <pre className="overflow-auto rounded-sm bg-bg-soft p-2 font-mono text-[11px]">
          {error.message}
          {error.stack ? `\n\n${error.stack}` : ''}
        </pre>
        <button
          type="button"
          className="btn mt-3"
          onClick={() => this.setState({ error: null })}
        >
          重试
        </button>
      </div>
    );
  }
}
